import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiCheck } from 'react-icons/fi';
import { Card, Button, Badge, Avatar, TextArea, DayLabel, SlotBadge, Spinner } from '../components/Components';
import { coachAPI, bookingAPI } from '../utils/api';
import toast from 'react-hot-toast';
import './Booking.css';

export default function BookSession() {
  const { coachId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [coach, setCoach] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);

  const [selectedDate, setSelectedDate] = useState(searchParams.get('date') || '');
  const [selectedTime, setSelectedTime] = useState(searchParams.get('time') || '');
  const [topic, setTopic] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [coachRes, availRes] = await Promise.all([
          coachAPI.getById(coachId),
          coachAPI.getAvailability(coachId),
        ]);
        setCoach(coachRes.data.coach);
        setAvailability(availRes.data.availability);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [coachId]);

  const handleBook = async () => {
    if (!selectedDate || !selectedTime) return toast.error('Please select a date and time');
    setBooking(true);
    try {
      await bookingAPI.create({
        coachId, date: selectedDate, time: selectedTime, topic, notes,
      });
      toast.success('Session booked successfully! 🎉');
      navigate('/bookings');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Booking failed');
    } finally { setBooking(false); }
  };

  if (loading) return <div className="page-container"><Spinner /></div>;
  if (!coach) return <div className="page-container"><p>Coach not found</p></div>;

  const initials = coach.avatar || coach.name?.split(' ').map(n => n[0]).join('');

  return (
    <div className="page-container" style={{ maxWidth: 800 }}>
      <Link to="/search" className="back-link"><FiArrowLeft size={14} /> Back to Search</Link>

      <h2 className="booking-title">Book a Session</h2>
      <p className="booking-subtitle">with {coach.name}</p>

      {/* Coach Mini Card */}
      <Card className="booking-coach-card">
        <div className="booking-coach-card__left">
          <Avatar initials={initials} size="lg" />
          <div>
            <div className="booking-coach-card__name">{coach.name}</div>
            <div className="booking-coach-card__badges">
              {coach.expertise?.map(e => <Badge key={e} variant="primary" size="sm">{e}</Badge>)}
            </div>
          </div>
        </div>
        <div className="booking-coach-card__rate">
          ${coach.rate}<span>/hr</span>
        </div>
      </Card>

      {/* Step 1: Select Date & Time — Day Wise, Today First */}
      <Card className="booking-step">
        <h3 className="booking-step__title">📅 Select Date & Time Slot</h3>
        {availability.length === 0 ? (
          <div className="booking-empty">No available slots. Please check back later.</div>
        ) : (
          <div className="booking-slots">
            {availability.map(({ date, label, slots }) => (
              <div key={date} className="booking-slot-day">
                <DayLabel
                  label={label}
                  date={new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                />
                <div className="booking-slot-times">
                  {slots.map(time => (
                    <SlotBadge
                      key={time}
                      time={time}
                      selected={selectedDate === date && selectedTime === time}
                      onClick={() => { setSelectedDate(date); setSelectedTime(time); }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Step 2: Topic & Notes */}
      <Card className="booking-step">
        <h3 className="booking-step__title">📝 Session Details (Optional)</h3>
        <div className="input-group">
          <label className="input-label">Topic</label>
          <input
            className="input-field"
            placeholder="e.g., Career transition, Interview prep..."
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
          />
        </div>
        <TextArea
          label="Notes for the coach"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What would you like to discuss? Any specific goals?"
        />
      </Card>

      {/* Summary */}
      {selectedDate && selectedTime && (
        <Card className="booking-summary">
          <h3 className="booking-summary__title"><FiCheck size={16} /> Booking Summary</h3>
          <div className="booking-summary__grid">
            <div><strong>Coach:</strong> {coach.name}</div>
            <div><strong>Rate:</strong> ${coach.rate}/hr</div>
            <div><strong>Date:</strong> {availability.find(a => a.date === selectedDate)?.label} — {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
            <div><strong>Time:</strong> {selectedTime}</div>
            <div><strong>Duration:</strong> {coach.sessionDuration || 60} minutes</div>
            {topic && <div><strong>Topic:</strong> {topic}</div>}
          </div>
        </Card>
      )}

      <Button
        variant="success"
        fullWidth
        size="lg"
        disabled={!selectedDate || !selectedTime || booking}
        onClick={handleBook}
      >
        {booking ? 'Booking...' : 'Confirm Booking →'}
      </Button>
    </div>
  );
}
